import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

import cetasolLogo from "@/assets/cetasol-logo.png";
import { Linkedin, Facebook, Youtube, Instagram } from "lucide-react";

const NewsletterForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Load HubSpot script
    const script = document.createElement('script');
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
    
    script.onload = () => {
      // @ts-ignore
      if (window.hbspt) {
        // @ts-ignore
        window.hbspt.forms.create({
          portalId: "25538277",
          formId: "696442d1-e7ad-4317-9746-d6734d123e5f",
          region: "eu1",
          target: '#footer-newsletter-form'
        });
        
        // Inject custom CSS after form loads
        setTimeout(() => {
          const injectCSS = () => {
            const container = document.getElementById('footer-newsletter-form');
            if (!container) return false;
            
            const iframe = container.querySelector('iframe');
            if (!iframe) return false;
            
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              if (!iframeDoc) return false;
              
              if (iframeDoc.getElementById('hs-custom-css')) return true;
              
              const style = iframeDoc.createElement('style');
              style.id = 'hs-custom-css';
              style.textContent = `
                .hs-button, input[type="submit"] {
                  background-color: #1679B1 !important;
                  color: #fff !important;
                  border: none !important;
                  border-radius: 0.375rem !important;
                  padding: 0.75rem 1.5rem !important;
                  font-size: 0.875rem !important;
                  font-weight: 500 !important;
                  font-family: 'Montserrat', sans-serif !important;
                  cursor: pointer !important;
                  width: 100% !important;
                  transition: background-color 0.2s !important;
                }
                .hs-button:hover, input[type="submit"]:hover {
                  background-color: #14699B !important;
                }
                .legal-consent-container a, .hs-richtext a {
                  color: #1679B1 !important;
                  text-decoration: underline !important;
                }
                .legal-consent-container a:hover, .hs-richtext a:hover {
                  color: #14699B !important;
                }
              `;
              iframeDoc.head.appendChild(style);
              return true;
            } catch {
              return false;
            }
          };
          
          // Try to inject CSS multiple times
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if (injectCSS() || attempts > 50) {
              clearInterval(interval);
            }
          }, 200);
        }, 500);
      }
    };
    
    document.body.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[src="//js-eu1.hsforms.net/forms/embed/v2.js"]');
      if (existingScript && document.body.contains(existingScript)) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);
  
  return <div id="footer-newsletter-form" ref={containerRef} className="min-h-[200px]" />;
};

const Footer = () => {
  const { t } = useTranslation('common');
  
  return (
    <footer className="section-fade">
      <div className="container mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8 md:mb-12">
          <img src={cetasolLogo} alt="Cetasol" className="h-10" />
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/company/cetasol/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="Cetasol on LinkedIn" className="text-muted-foreground hover:text-foreground transition-colors">
              <Linkedin size={22} aria-hidden="true" />
            </a>
            <a href="https://www.facebook.com/cetasol" target="_blank" rel="noopener noreferrer" aria-label="Cetasol on Facebook" className="text-muted-foreground hover:text-foreground transition-colors">
              <Facebook size={22} aria-hidden="true" />
            </a>
            <a href="https://www.youtube.com/@cetasol" target="_blank" rel="noopener noreferrer" aria-label="Cetasol on YouTube" className="text-muted-foreground hover:text-foreground transition-colors">
              <Youtube size={22} aria-hidden="true" />
            </a>
            <a href="https://www.instagram.com/cetasol/" target="_blank" rel="noopener noreferrer" aria-label="Cetasol on Instagram" className="text-muted-foreground hover:text-foreground transition-colors">
              <Instagram size={22} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-12">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground tracking-wide mb-6">
              {t('footer.subscribe')}
            </h3>
            <NewsletterForm />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div>
                <h4 className="font-display font-bold text-muted-foreground/50 text-xs uppercase tracking-wider mb-2">{t('footer.swedenHq')}</h4>
                <p className="text-xs text-muted-foreground/50 whitespace-pre-line leading-relaxed">{t('footer.swedenAddress')}</p>
              </div>
              <div>
                <h4 className="font-display font-bold text-muted-foreground/50 text-xs uppercase tracking-wider mb-2">{t('footer.canadaOffice')}</h4>
                <p className="text-xs text-muted-foreground/50 leading-relaxed">1788 West Broadway,<br />Suite 703 Vancouver,<br />BC, Canada V6J 1Y1</p>
              </div>
              <div>
                <h4 className="font-display font-bold text-muted-foreground/50 text-xs uppercase tracking-wider mb-2">{t('footer.singaporeOffice')}</h4>
                <p className="text-xs text-muted-foreground/50 leading-relaxed">77 Ayer Rajah<br />Crescent #02-28,<br />Singapore 139954</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="font-display font-bold text-foreground text-sm uppercase tracking-wider mb-4">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/ihelm-intelligent-platform" className="text-sm text-muted-foreground hover:text-foreground transition-colors">iHelm</Link></li>
                <li><Link to="/cetafuel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">CetaFuel</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-foreground text-sm uppercase tracking-wider mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.aboutUs')}</Link></li>
                <li><Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.contact')}</Link></li>
                <li><Link to="/career-at-cetasol-team" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.careers')}</Link></li>
                <li><Link to="/security" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.security')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-foreground text-sm uppercase tracking-wider mb-4">{t('footer.resources')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/insight/blog-post" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.blogPost')}</Link></li>
                <li><Link to="/news" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.news')}</Link></li>
                <li><Link to="/insight/press-release" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.pressRelease')}</Link></li>
                <li><Link to="/whitepapers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t('footer.whitePapers')}</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('footer.privacyPolicy')}</Link>
            <Link to="/terms-conditions" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('footer.termsOfService')}</Link>
            <Link to="/admin/blog" className="text-xs text-muted-foreground/40 hover:text-foreground transition-colors">{t('footer.admin')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
